import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Copy,
  ExternalLink,
  X,
} from "lucide-react";
import { courseApi, uploadImage } from "./adminApi";
import RichTextEditor from "./RichTextEditor";
import { htmlToLines, linesToHtml } from "../utils/richText";
import "./Admin.css";

const emptySection = () => ({ heading: "", paragraphs: "", bullets: "", closing: "" });

const emptyForm = {
  name: "",
  slug: "",
  code: "",
  category: "",
  level: "",
  tagline: "",
  duration: "",
  thumbnail: "",
  isPublished: true,
  order: 0,
  intro: "",
  syllabus: "",
  highlights: "",
  seoTitle: "",
  seoMetaDescription: "",
  sections: [emptySection()],
};

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Convert the API course shape (arrays of lines) into the form's shape.
// Intro / paragraphs / bullets / closing are edited in rich-text editors, so
// they are held as HTML in the form (one <p> per line) and turned back into
// arrays of single-line strings on save (see formToPayload).
function courseToForm(course) {
  return {
    name: course.name || "",
    slug: course.slug || "",
    code: course.code || "",
    category: course.category || "",
    level: course.level || "",
    tagline: course.tagline || "",
    duration: course.duration || "",
    thumbnail: course.thumbnail || "",
    isPublished: course.isPublished !== false,
    order: course.order || 0,
    intro: linesToHtml(course.detail?.intro),
    syllabus: (course.detail?.syllabus || []).join("\n"),
    highlights: (course.highlights || []).join("\n"),
    seoTitle: course.detail?.seo?.title || "",
    seoMetaDescription: course.detail?.seo?.metaDescription || "",
    sections:
      course.detail?.sections?.length > 0
        ? course.detail.sections.map((s) => ({
            heading: s.heading || "",
            paragraphs: linesToHtml(s.paragraphs),
            bullets: linesToHtml(s.bullets),
            closing: linesToHtml(s.closing ? [s.closing] : []),
          }))
        : [emptySection()],
  };
}

// Convert the form's shape back into the API shape.
function formToPayload(form) {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    code: form.code.trim(),
    category: form.category.trim(),
    level: form.level.trim(),
    tagline: form.tagline.trim(),
    duration: form.duration.trim(),
    thumbnail: form.thumbnail.trim(),
    isPublished: !!form.isPublished,
    order: Number(form.order) || 0,
    highlights: form.highlights
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean),
    detail: {
      intro: htmlToLines(form.intro),
      syllabus: form.syllabus.split("\n").map((l) => l.trim()).filter(Boolean),
      seo: {
        title: form.seoTitle.trim(),
        metaDescription: form.seoMetaDescription.trim(),
      },
      sections: form.sections
        .filter((s) => s.heading.trim() || s.paragraphs.trim() || s.bullets.trim())
        .map((s) => ({
          heading: s.heading.trim(),
          paragraphs: htmlToLines(s.paragraphs),
          bullets: htmlToLines(s.bullets),
          closing: htmlToLines(s.closing).join("<br>"),
        })),
    },
  };
}

// Small helper so main-detail fields and description-style fields get
// visually distinct label treatment (bold + highlighted vs. lighter).
// See .admin-field--main / .admin-field--desc in Admin.css.
function Field({ label, hint, tone = "main", children, charCount }) {
  return (
    <div className={`admin-field admin-field--${tone}`}>
      <div className="admin-field__label-row">
        <label>{label}</label>
        {charCount != null && <span className="admin-field__count">{charCount}</span>}
      </div>
      {children}
      {hint && <span className="admin-field__hint">{hint}</span>}
    </div>
  );
}

export default function CourseForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [slugTouched, setSlugTouched] = useState(isEdit); // don't auto-slug when editing an existing course

  useEffect(() => {
    if (!isEdit) {
      setInitialForm(emptyForm);
      return;
    }
    let cancelled = false;

    async function load() {
      try {
        const data = await courseApi.listAll();
        const course = data.find((c) => c._id === id);
        if (!course) throw new Error("Course not found.");
        if (!cancelled) {
          const shaped = courseToForm(course);
          setForm(shaped);
          setInitialForm(shaped);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const isDirty = useMemo(() => {
    if (!initialForm) return false;
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  // Warn before leaving the tab with unsaved changes — cheap insurance
  // against losing a half-written course page.
  useEffect(() => {
    function handler(e) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleNameChange(value) {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }));
  }

  function handleSlugChange(value) {
    setSlugTouched(true);
    update("slug", value);
  }

  function updateSection(index, field, value) {
    setForm((prev) => {
      const sections = [...prev.sections];
      sections[index] = { ...sections[index], [field]: value };
      return { ...prev, sections };
    });
  }

  function addSection() {
    setForm((prev) => ({ ...prev, sections: [...prev.sections, emptySection()] }));
  }

  function removeSection(index) {
    setForm((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== index) }));
  }

  function duplicateSection(index) {
    setForm((prev) => {
      const sections = [...prev.sections];
      sections.splice(index + 1, 0, { ...sections[index] });
      return { ...prev, sections };
    });
  }

  function moveSection(index, direction) {
    setForm((prev) => {
      const sections = [...prev.sections];
      const target = index + direction;
      if (target < 0 || target >= sections.length) return prev;
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return { ...prev, sections };
    });
  }

  async function handleThumbnailUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const url = await uploadImage(file);
      update("thumbnail", url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name.trim()) {
      setError("Course name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = formToPayload(form);
      let saved;
      if (isEdit) {
        saved = await courseApi.update(id, payload);
      } else {
        saved = await courseApi.create(payload);
      }
      setInitialForm(form);
      setSuccess(isEdit ? "Course updated." : "Course created.");
      // Give the success message a beat to show, then head back to the list.
      setTimeout(() => navigate("/admin/courses"), 500);
      return saved;
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate() {
    if (!isEdit) return;
    setDuplicating(true);
    setError("");
    try {
      const clone = await courseApi.duplicate(id);
      navigate(`/admin/courses/${clone._id}/edit`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDuplicating(false);
    }
  }

  if (loading) return <p>Loading…</p>;

  const seoTitleLen = form.seoTitle.length;
  const seoDescLen = form.seoMetaDescription.length;

  return (
    <div>
      <div className="admin-topbar">
        <h1>{isEdit ? "Edit Course" : "Add Course"}</h1>
        <div style={{ display: "flex", gap: "0.6rem" }}>
          {isEdit && form.slug && (
            <a
              className="admin-btn admin-btn--outline"
              style={{ width: "auto" }}
              href={`/courses/${form.slug}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={16} /> View Live
            </a>
          )}
          {isEdit && (
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              style={{ width: "auto" }}
              onClick={handleDuplicate}
              disabled={duplicating}
            >
              <Copy size={16} /> {duplicating ? "Duplicating…" : "Duplicate"}
            </button>
          )}
          <Link to="/admin/courses" className="admin-btn admin-btn--outline" style={{ width: "auto" }}>
            <ArrowLeft size={16} /> Back to Courses
          </Link>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-success">{success}</div>}
      {isDirty && !saving && (
        <div className="admin-unsaved-banner">You have unsaved changes.</div>
      )}

      <form className="admin-form" onSubmit={handleSubmit}>
        {/* ── BASICS — the core identifying details, bold + highlighted ── */}
        <div className="admin-form__section">
          <h3>Basics</h3>

          <div className="admin-form__row">
            <Field label="Course Name *">
              <input
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Python"
                required
              />
            </Field>
            <Field
              label="Slug (URL key)"
              hint={form.slug ? `Live at /courses/${form.slug}` : "Auto-generated from the course name"}
            >
              <input
                value={form.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="e.g. python"
              />
            </Field>
          </div>

          <div className="admin-form__row">
            <Field label="Code">
              <input value={form.code} onChange={(e) => update("code", e.target.value)} placeholder="e.g. PY" />
            </Field>
            <Field label="Category">
              <input
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                placeholder="e.g. Programming"
              />
            </Field>
          </div>

          <div className="admin-form__row">
            <Field label="Level">
              <input
                value={form.level}
                onChange={(e) => update("level", e.target.value)}
                placeholder="e.g. Beginner Friendly"
              />
            </Field>
            <Field label="Duration">
              <input
                value={form.duration}
                onChange={(e) => update("duration", e.target.value)}
                placeholder="e.g. 8 Weeks"
              />
            </Field>
          </div>

          <div className="admin-form__row">
            <Field label="Display Order" hint="Lower numbers show first on the course list">
              <input type="number" value={form.order} onChange={(e) => update("order", e.target.value)} />
            </Field>
            <Field label="Status" tone="main">
              <div className="admin-toggle-row">
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={form.isPublished}
                  onChange={(e) => update("isPublished", e.target.checked)}
                />
                <label htmlFor="isPublished" className="admin-toggle-row__label">
                  {form.isPublished ? "Published (visible on the live site)" : "Draft (hidden from the live site)"}
                </label>
              </div>
            </Field>
          </div>

          <Field label="Tagline / Short Description" tone="desc" hint="One or two sentences shown under the course name">
            <textarea value={form.tagline} onChange={(e) => update("tagline", e.target.value)} rows={2} />
          </Field>

          <Field label="Thumbnail Image" tone="desc">
            <input type="file" accept="image/*" onChange={handleThumbnailUpload} disabled={uploading} />
            {uploading && <span style={{ fontSize: "0.8rem", color: "var(--c-mut)" }}>Uploading…</span>}
            {form.thumbnail && (
              <div className="admin-thumb-wrap">
                <img src={form.thumbnail} alt="Thumbnail preview" className="admin-thumb-preview" />
                <button
                  type="button"
                  className="admin-thumb-remove"
                  onClick={() => update("thumbnail", "")}
                  aria-label="Remove thumbnail"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <input
              value={form.thumbnail}
              onChange={(e) => update("thumbnail", e.target.value)}
              placeholder="or paste an image URL directly"
              style={{ marginTop: "0.5rem" }}
            />
          </Field>
        </div>

        {/* ── HIGHLIGHTS — short CTA checklist points, main emphasis ── */}
        <div className="admin-form__section">
          <h3>Highlights</h3>
          <Field
            label="Checklist Points (one per line)"
            hint='Shown with checkmarks near the enquiry buttons, e.g. "Learn live". Leave blank to use the site default.'
          >
            <textarea
              value={form.highlights}
              onChange={(e) => update("highlights", e.target.value)}
              rows={4}
              placeholder={"Learn live\nWrite code during class\nGet immediate feedback\nBuild projects with mentor guidance"}
            />
          </Field>
        </div>

        {/* ── LONG-FORM DETAILS — lighter, descriptive copy ── */}
        <div className="admin-form__section">
          <h3>Long-form Details</h3>

          <Field
            label="Intro Paragraphs"
            tone="desc"
            hint="Press Enter for a new paragraph. Select text and click B to make it bold."
          >
            <RichTextEditor
              mode="lines"
              value={form.intro}
              onChange={(html) => update("intro", html)}
              placeholder="Write the intro paragraphs…"
              minHeight={130}
            />
          </Field>

          <Field label="Syllabus / Modules (one per line)" tone="desc">
            <textarea value={form.syllabus} onChange={(e) => update("syllabus", e.target.value)} rows={4} />
          </Field>
        </div>

        {/* ── CONTENT SECTIONS — reorderable, duplicable blocks ── */}
        <div className="admin-form__section">
          <h3>Content Sections</h3>
          {form.sections.map((section, i) => (
            <div
              key={i}
              className="admin-section-block"
              style={{
                borderTop: i > 0 ? "1px dashed var(--c-line)" : "none",
                paddingTop: i > 0 ? "1rem" : 0,
                marginTop: i > 0 ? "1rem" : 0,
              }}
            >
              <div className="admin-section-block__toolbar">
                <span className="admin-section-block__index">Section {i + 1}</span>
                <div className="admin-section-block__actions">
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => moveSection(i, -1)}
                    disabled={i === 0}
                    aria-label="Move section up"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => moveSection(i, 1)}
                    disabled={i === form.sections.length - 1}
                    aria-label="Move section down"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => duplicateSection(i)}
                    aria-label="Duplicate section"
                    title="Duplicate"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              <Field label="Section Heading">
                <input
                  value={section.heading}
                  onChange={(e) => updateSection(i, "heading", e.target.value)}
                  placeholder="e.g. What You'll Learn"
                />
              </Field>
              <Field label="Paragraphs" tone="desc" hint="Press Enter for a new paragraph.">
                <RichTextEditor
                  mode="lines"
                  value={section.paragraphs}
                  onChange={(html) => updateSection(i, "paragraphs", html)}
                  placeholder="Write the paragraphs for this section…"
                  minHeight={100}
                />
              </Field>
              <Field label="Bullet Points" tone="desc" hint="Press Enter for each new bullet point.">
                <RichTextEditor
                  mode="lines"
                  value={section.bullets}
                  onChange={(html) => updateSection(i, "bullets", html)}
                  placeholder="One bullet point per line…"
                  minHeight={100}
                />
              </Field>
              <Field label="Closing Line (optional)" tone="desc" hint="Shown after the bullet points">
                <RichTextEditor
                  mode="lines"
                  value={section.closing}
                  onChange={(html) => updateSection(i, "closing", html)}
                  placeholder="e.g. No previous experience is required."
                  minHeight={56}
                />
              </Field>

              {form.sections.length > 1 && (
                <button
                  type="button"
                  className="admin-btn admin-btn--danger admin-btn--sm"
                  onClick={() => removeSection(i)}
                >
                  <Trash2 size={14} /> Remove Section
                </button>
              )}
            </div>
          ))}

          <button type="button" className="admin-btn admin-btn--outline admin-btn--sm" onClick={addSection} style={{ marginTop: "1rem" }}>
            <Plus size={14} /> Add Section
          </button>
        </div>

        {/* ── SEO — optional overrides, lighter emphasis ── */}
        <div className="admin-form__section">
          <h3>SEO (optional)</h3>
          <Field
            label="Page Title Override"
            tone="desc"
            charCount={`${seoTitleLen}/60`}
            hint="Leave blank to auto-generate from the course name"
          >
            <input
              value={form.seoTitle}
              onChange={(e) => update("seoTitle", e.target.value)}
              placeholder={`${form.name || "Course"} Course Online | Live Classes | What Next`}
            />
          </Field>
          <Field
            label="Meta Description Override"
            tone="desc"
            charCount={`${seoDescLen}/160`}
            hint="Leave blank to auto-generate from the course name"
          >
            <textarea
              value={form.seoMetaDescription}
              onChange={(e) => update("seoMetaDescription", e.target.value)}
              rows={2}
            />
          </Field>
        </div>

        <div className="admin-form__actions">
          <button type="submit" className="admin-btn admin-btn--primary" style={{ width: "auto" }} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Course"}
          </button>
          <Link to="/admin/courses" className="admin-btn admin-btn--outline">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}